import { Component, Input, Optional, Self } from '@angular/core';
import {
  ControlValueAccessor,
  FormControlName,
  NgControl
} from '@angular/forms';
import { SizeType } from '../../../types/common.types';

@Component({
  selector: 'idp-text-area',
  templateUrl: './text-area.component.html',
  styleUrls: ['./text-area.component.scss']
})
export class TextAreaComponent implements ControlValueAccessor {
  @Input() addMargin = false;
  @Input() addtlMaskInfo = false;
  @Input() arrowPosition = 'right';
  @Input() charLimit!: number;
  @Input() disabled = false;
  @Input() error = false;
  @Input() errorMessage = '';
  @Input() errorMessages: { [key: string]: string } = {};
  @Input() id = '';
  @Input() label = '';
  @Input() submitAttempt = false;
  @Input() value = '';
  @Input() sizeType: SizeType = 'normal';
  focused = false;

  private onChange!: (item: { id: string; value: string }) => void;
  private onTouched: (() => void) | undefined;

  constructor(
    @Optional() @Self() private ngControl: NgControl,
    @Optional() private controlName: FormControlName
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  get errorState() {
    if (this.validationCheck1() && this.validationCheck2()) {
      this.errorMessage =
        this.errorMessages[
          Object.keys(this.ngControl.errors ? this.ngControl.errors : '')[0]
        ];
    }

    return this.validationCheck1();
  }

  getClasses(): string {
    return `idp-textarea idp-textarea__${
      this.sizeType
    } ${this.getErrorStyle()} ${this.getMargin()}`;
  }

  getMargin(): string {
    return this.addMargin ? 'idp-textarea__add-margin' : '';
  }

  getErrorStyle(): string {
    return this.errorState || this.error ? 'idp-textarea--error' : '';
  }

  setValue(event: KeyboardEvent) {
    if (this.onChange) {
      const eventTarget = event.target as HTMLInputElement;
      if (this.eventCondition(event)) {
        this.onChange({
          id: this.id,
          value: eventTarget.value
        });
      }
    }
  }

  eventCondition(event: KeyboardEvent) {
    const eventTarget = event.target as HTMLInputElement;
    return event && eventTarget.value && eventTarget.value !== '';
  }

  setValuePaste(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData;
    if (event.type === 'paste') {
      if (
        clipboardData?.getData('Text') &&
        event.clipboardData?.getData('Text') !== ''
      ) {
        this.processOnChange(event);
      }
    }
    return;
  }

  processOnChange(event: ClipboardEvent) {
    if (this.onChange) {
      setTimeout(() => {
        if (this.value && this.value !== null) {
          this.onChange?.({
            id: this.id,
            value: this.value + event.clipboardData?.getData('Text')
          });
        } else {
          this.onChange?.({
            id: this.id,
            value: event.clipboardData?.getData('Text') ?? ''
          });
        }
      }, 0);
    }
  }

  writeValue(obj: string) {
    this.value = obj;
  }

  registerOnChange(fn: (item: { id: string; value: string }) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  validationCheck1() {
    return (
      this.ngControl &&
      this.ngControl.errors &&
      this.ngControl.touched &&
      this.submitAttempt
    );
  }

  validationCheck2() {
    return Object.keys(this.errorMessages).length;
  }
}
